import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SOCIAL_LINKS } from '../constants';
import { BidMasterLogo } from './BidMasterLogo';

const footHeading = 'font-black uppercase tracking-widest text-[10px] text-blue-500 mb-6';
const footLink = 'text-gray-400 font-bold text-sm hover:text-white transition-colors';

function SocialIcon({ children, label, href }: { children: ReactNode; label: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-400 hover:text-white transition-colors"
      aria-label={label}
    >
      {children}
    </a>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-[#0f172a] text-white py-16 md:py-20 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-10 lg:gap-12 mb-16 md:mb-20">
          <div className="md:col-span-2">
            <div className="mb-8">
              <BidMasterLogo
                href="/"
                className="hover:opacity-90 transition-opacity"
                textClassName="font-black text-2xl tracking-tight text-white"
              />
            </div>
            <p className="text-gray-400 font-medium max-w-md leading-relaxed">
              Empowering businesses with real-time bidding technology and highest Cost &amp; Time saving procurement
              insights.
            </p>
          </div>

          <div>
            <h4 className={footHeading}>Product</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/#features" className={footLink}>
                  Features
                </Link>
              </li>
              <li>
                <Link to="/signup" className={footLink}>
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/#download" className={footLink}>
                  Download App
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={footHeading}>Company</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/about" className={footLink}>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className={footLink}>
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/privacy" className={footLink}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className={footLink}>
                  Terms of Use
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-10 md:pt-12 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="text-gray-500 text-xs font-bold order-2 sm:order-1">Prav Solutions. All rights reserved.</p>
          <div className="flex items-center gap-5 order-1 sm:order-2">
            <SocialIcon label="LinkedIn" href={SOCIAL_LINKS.linkedIn}>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </SocialIcon>
            <SocialIcon label="Instagram" href={SOCIAL_LINKS.instagram}>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.315 2c2.43 0 2.784.013 3.806.058 1.065.056 1.995.24 2.724.511.734.276 1.366.64 1.998 1.272.632.632.996 1.264 1.273 1.998.27.729.455 1.659.51 2.724.046 1.022.059 1.377.059 3.806s-.013 2.784-.058 3.806-.24 1.995-.511 2.724-.276.734-.64 1.366-1.272 1.998-.632.632-1.264.996-1.998 1.273-.729.27-1.659.455-2.724.51-1.023.046-1.377.059-3.807.059s-2.784-.013-3.806-.058c-1.065-.056-1.995-.24-2.724-.511-.734-.276-1.366-.64-1.998-1.272-.632-.632-.996-1.264-1.273-1.998-.27-.729-.455-1.659-.51-2.724-.046-1.022-.059-1.377-.059-3.806s.013-2.784.058-3.806.24-1.995.511-2.724.276-.734.64-1.366 1.272-1.998.632-.632 1.264-.996 1.998-1.273.729-.27 1.659-.455 2.724-.51 1.023-.046 1.377-.059 3.807-.059zm0 1.802c-2.38 0-2.681.01-3.619.052-.864.039-1.333.176-1.648.29-.415.161-.71.355-.987.632-.277.277-.471.572-.632.987-.114.315-.251.784-.29 1.648-.043.938-.053 1.239-.053 3.619s.01 2.681.053 3.619c.039.864.176 1.333.29 1.648.161.415.355.71.632.987.277.277.572.471.987.632.315.114.784.251 1.648.29.938.043 1.239.053 3.619.053s2.681-.01 3.619-.052c.864-.039 1.333-.176 1.648-.29.415-.161.71-.355.987-.632.277-.277.471-.572.632-.987.114-.315.251-.784.29-1.648.043-.938.053-1.239.053-3.619s-.01-2.681-.052-3.619c-.039-.864-.176-1.333-.29-1.648-.161-.415-.355-.71-.632-.987-.277-.277-.572-.471-.987-.632-.315-.114-.784-.251-1.648-.29-.938-.043-1.239-.053-3.619-.053zm0 2.77c-2.476 0-4.481 2.005-4.481 4.481s2.005 4.481 4.481 4.481 4.481-2.005 4.481-4.481-2.005-4.481-4.481-4.481zm0 7.393c-1.609 0-2.912-1.303-2.912-2.912s1.303-2.912 2.912-2.912 2.912 1.303 2.912 2.912-1.303 2.912-2.912 2.912zm4.595-7.561c0 .588-.476 1.064-1.064 1.064s-1.064-.476-1.064-1.064.476-1.064 1.064-1.064 1.064.476 1.064 1.064z"
                />
              </svg>
            </SocialIcon>
            <SocialIcon label="Facebook" href={SOCIAL_LINKS.facebook}>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </SocialIcon>
          </div>
        </div>
      </div>
    </footer>
  );
}
